// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  spacing,
  mediaMax,
  fontWeight,
  fontSize,
} from 'presentations/views/styles';
import WeeklyEventTypeTimeList from 'presentations/views/components/organisms/WeeklyEventTypeTimeList';
import WeekCalendar from 'presentations/views/components/organisms/WeeklyEventTypeCalendar';
import WeekCalendarLegacy from 'presentations/views/components/organisms/WeeklyEventTypeCalendar/legacy';
import { DateTime, getDateTime } from 'infras/datetime';
import { black, gray01, gray04 } from 'presentations/views/styles/color';
import { TimeZoneListItem } from 'presentations/api/schema/time_zone';
import Select from 'presentations/views/components/atoms/Select';
import Icon from 'presentations/views/components/atoms/Icon';
import {
  TimeListViewerCalendarEvent,
  ViewerCalendarEventListItem,
} from 'presentations/api/schema/calendar_event';
import { SharedEventTypeItem } from 'presentations/api/schema/event_type';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import { StartWeekDay } from 'domains/models/event_type/startWeekDay';
import { SyntheticEvent, useMemo, useState } from 'react';
import { TimeList } from 'presentations/api/helpers/event_type';
import DateController, {
  DateControllerChangeType,
} from './components/DateController';
import TimeZoneListPopover from 'presentations/views/components/organisms/TimeZoneListPopover';
import { CalendarWeekDay } from 'presentations/views/components/atoms/Calendar/reducer';

interface Props {
  eventType: SharedEventTypeItem;
  viewType: CalendarViewType;
  onDateSelect: (selected: DateTime) => void;
  selectedStartDate: DateTime | null;
  selectedDatetime: DateTime | null;
  selectedDatetimes?: DateTime[];
  allTimeLists: TimeList[];
  timeLists: TimeList[];
  onTimeSelect?: any;
  isLoading?: boolean;
  // onTimeZoneSelect?: any;
  updateTimeZone?: (timeZone: string) => void;
  timeZones: TimeZoneListItem[];
  timeZone: string;
  isLightColor: boolean;
  viewerBlocks: ViewerCalendarEventListItem[];
  viewerTeamUser?: TeamUserListItem;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode?: boolean;
  onClickBlockTime?: Function;
  isAllSelect?: boolean;
  isDisabledAllSelect?: boolean;
  onClickAllSelect?: Function;
  isPoll?: boolean;
  startWeekDay: StartWeekDay;
  isFetching: boolean;
  displayWeekdays?: number[];
  onClickEvent?: (e: HTMLElement, event: TimeListViewerCalendarEvent) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const [timeZoneAnchor, setTimeZoneAnchor] = useState<HTMLElement | null>(
    null,
  );

  const isAvailableWeekly = useMemo(() => {
    return props.timeLists.some((t) => t.length > 0);
  }, [props.timeLists]);

  const today = getDateTime(null, props.timeZone);
  const selectedStartDate = props.selectedStartDate;

  const onCloseTimeZone = () => {
    setTimeZoneAnchor(null);
  };

  const onClickTimeZone = (e: SyntheticEvent<HTMLDivElement>) => {
    setTimeZoneAnchor(e.currentTarget);
  };

  const onSelectTimeZone = (name: string) => {
    if (!props.updateTimeZone) return;
    props.updateTimeZone(name);
    onCloseTimeZone();
  };

  const handleChangeTerm = (action: DateControllerChangeType) => {
    switch (action) {
      case 'prev':
        if (!selectedStartDate) return;
        props.onDateSelect(selectedStartDate.minus({ days: 7 }));
        break;
      case 'today':
        if (!selectedStartDate) return;
        const subWeekdays = today.weekday - props.startWeekDay;
        props.onDateSelect(today.minus({ days: subWeekdays }).startOf('day'));
        break;
      case 'next':
        if (!selectedStartDate) return;
        props.onDateSelect(selectedStartDate.plus({ days: 7 }));
        break;
      default:
        break;
    }
  };

  const onChangeStartDate = (date: DateTime) => {
    props.onDateSelect(date);
  };

  const onClickCalendarUnavailableButton = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    handleChangeTerm('next');
  };

  return (
    <PickerContainer>
      {selectedStartDate && (
        <>
          <TitleContainer>
            <Title>{selectedStartDate.toFormat(t('title'))}</Title>
            <ControlContainer>
              <TimeZoneContainer>
                {!props.isLoading && <TimeZoneIcon name="access_time" />}
                <TimeZoneContainer onClick={onClickTimeZone}>
                  <TimeZoneSelect
                    // width={160}
                    loadingWidth={100}
                    isLoading={props.isLoading}
                    isSkeleton={true}
                    name="timeZone"
                    size="small"
                    variant="standard"
                    value={props.timeZone}
                    options={[
                      {
                        value: props.timeZone,
                        text: props.timeZone,
                      },
                    ]}
                    color={props.eventType.color}
                  />
                </TimeZoneContainer>
              </TimeZoneContainer>
              <StyledDateControllerContainer>
                <DateController
                  isDisabledPrevButton={
                    props.isLoading ||
                    selectedStartDate?.until(today).toDuration().days <= 7
                  }
                  isDisabledTodayButton={Boolean(props.isLoading)}
                  isDisabledNextButton={Boolean(props.isLoading)}
                  handleChangeTerm={handleChangeTerm}
                />
              </StyledDateControllerContainer>
            </ControlContainer>
          </TitleContainer>
          {props.onClickAllSelect && (
            <AllSelectContainer>
              <Checkbox
                name="allSelect"
                value={1}
                onChange={props.onClickAllSelect}
                isChecked={props.isAllSelect}
                label={<AllSelectLabel>{t('SelectAll')}</AllSelectLabel>}
                isDisabled={props.isDisabledAllSelect}
                specifyColor={props.eventType.color}
                isLoading={props.isLoading}
              />
            </AllSelectContainer>
          )}
          {props.viewType === CalendarViewType.Week && (
            <WeeklyEventTypeTimeList
              today={today}
              selectedStartDate={selectedStartDate}
              selectedDatetime={props.selectedDatetime}
              selectedDatetimes={props.selectedDatetimes}
              onSelect={props.onTimeSelect}
              allTimeLists={props.allTimeLists}
              timeLists={props.timeLists}
              isLoading={props.isLoading}
              timeZone={props.timeZone}
              color={props.eventType.color}
              isLightColor={props.isLightColor}
              isRevealGuestUser={props.isRevealGuestUser}
              isShowViewerCalendar={props.isShowViewerCalendar}
              isHostDiagnosticMode={props.isHostDiagnosticMode}
              onClickBlockTime={props.onClickBlockTime}
              isPoll={props.isPoll}
              isAvailable={isAvailableWeekly}
              isFetching={props.isFetching}
              startWeekday={props.startWeekDay}
              displayWeekdays={props.displayWeekdays}
              onClickCalendarUnavailableButton={
                onClickCalendarUnavailableButton
              }
            />
          )}
          {props.viewType === CalendarViewType.WeekCalendar && (
            <WeekCalendar
              selectedStartDate={selectedStartDate}
              selectedDatetime={props.selectedDatetime}
              timeZone={props.timeZone}
              isLoading={props.isLoading}
              duration={props.eventType.duration}
              interval={props.eventType.intervalMinutes}
              startWeekday={props.startWeekDay}
              color={props.eventType.color}
              isHostDiagnosticMode={props.isHostDiagnosticMode}
              timeLists={props.timeLists}
              viewerBlocks={props.viewerBlocks}
              allTimeLists={props.allTimeLists}
              viewerTeamUser={props.viewerTeamUser}
              isRevealGuestUser={props.isRevealGuestUser}
              isShowViewerCalendar={props.isShowViewerCalendar}
              onSelect={props.onTimeSelect}
              onClickBlockTime={props.onClickBlockTime}
              onClickEvent={props.onClickEvent}
              displayWeekdays={props.displayWeekdays as CalendarWeekDay[]}
              onClickCalendarUnavailableButton={
                onClickCalendarUnavailableButton
              }
            />
          )}
          {props.viewType === CalendarViewType.CalendarWeekLegacy && (
            <WeekCalendarLegacy
              today={today}
              selectedStartDate={selectedStartDate}
              selectedDatetime={props.selectedDatetime}
              onSelect={props.onTimeSelect}
              allTimeLists={props.allTimeLists}
              timeLists={props.timeLists}
              isLoading={props.isLoading}
              timeZone={props.timeZone}
              color={props.eventType.color}
              isLightColor={props.isLightColor}
              duration={props.eventType.duration}
              intervalMinutes={props.eventType.intervalMinutes}
              viewerBlocks={props.viewerBlocks}
              viewerTeamUser={props.viewerTeamUser}
              isRevealGuestUser={props.isRevealGuestUser}
              isShowViewerCalendar={props.isShowViewerCalendar}
              isHostDiagnosticMode={props.isHostDiagnosticMode}
              onClickBlockTime={props.onClickBlockTime}
              isAvailable={isAvailableWeekly}
              isFetching={props.isFetching}
              startWeekday={props.startWeekDay}
              displayWeekdays={props.displayWeekdays}
              onClickCalendarUnavailableButton={
                onClickCalendarUnavailableButton
              }
            />
          )}
        </>
      )}
      {timeZoneAnchor && (
        <TimeZoneListPopover
          title={t('Edit time zone')}
          anchor={timeZoneAnchor}
          onClose={onCloseTimeZone}
          onSelect={onSelectTimeZone}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}
    </PickerContainer>
  );
}

const PickerContainer = styled.div``;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: ${spacing(20)};
  border-bottom: 1px solid ${gray04};

  ${mediaMax.tablet} {
    padding: 0 ${spacing(12)} ${spacing(16)};
    margin: 0 0 ${spacing(8)};
  }
  ${mediaMax.smartphone} {
    flex-direction: column;
  }
`;
const ControlContainer = styled.div`
  display: flex;
  align-items: center;

  ${mediaMax.smartphone} {
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
    margin-top: ${spacing(16)};
  }
`;
const Title = styled.div`
  font-size: ${fontSize(20)};
  color: ${black};
  font-weight: ${fontWeight('bold')};
  flex-grow: 1;

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
  }
`;

const TimeZoneContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 ${spacing(12)};
`;
const TimeZoneIcon = styled(Icon)`
  color: ${gray01};
  /* margin-right: ${spacing(4)}; */
  font-size: ${fontSize(18)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const TimeZoneSelect = styled(Select)<{ color: string }>`
  border: 0;
  /* width: 160px; */
  /* ${mediaMax.tablet} {
    width: 100px;
  } */

  select {
    color: ${gray01};
    font-size: ${fontSize(14)};
    padding-bottom: 0;
    ${mediaMax.tablet} {
      padding-bottom: 0;
      padding-top: ${spacing(3)};
      height: 100%;
      font-size: ${fontSize(10)};
    }
  }
  .MuiInput-underline:before {
    border: 0;
  }
  .MuiInput-underline:after {
    border-color: ${({ color }) => color};
  }
`;
const StyledDateControllerContainer = styled.div`
  margin-left: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-left: 0;
  }
`;
const AllSelectContainer = styled.div`
  margin: 0 0 ${spacing(12)};

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(8)};
  }
`;
const AllSelectLabel = styled.span`
  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
